import React, { useState, useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  MoreVert as MoreVertIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Spa as SpaIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import axios from 'axios';
import ConfirmationDialog from '../components/ConfirmationDialog';

const EMPTY_USER = {
  username: "",
  email: "",
  phone_number: "",
  password: "",
  roles: [],
  taxRegistrationNumber: "",
  userERP: "",
  maxProfiles: "",
  country: "TN",
  trial_days: 30,
  afficher_traitement_limit: 50,
};

const SUBSCRIPTION_FILTERS = [
  { value: "all", label: "All subscriptions" },
  { value: "trial", label: "Trial" },
  { value: "paid", label: "Subscribed (Paid)" },
  { value: "not_subscribed", label: "Not subscribed" },
];

const authHeaders = () => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  return {
    "x-access-token": token,
    Authorization: `Bearer ${token}`,
  };
};

const errorMessage = (error, fallback) =>
  (error && error.response && error.response.data && error.response.data.message) || fallback;

// 'trial' | 'paid' | 'not_subscribed', kept in sync with the backend's subscription_mode
const getSubscriptionState = (user) => {
  if (!user.is_subscribed) return "not_subscribed";

  return user.subscription_mode === "trial" ? "trial" : "paid";
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);

  return isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
};

// A user still flagged as subscribed past their end date is pending expiry
const isSubscriptionLapsed = (user) => {
  if (!user.is_subscribed || !user.subscription_end_date) return false;
  const end = new Date(user.subscription_end_date);

  return !isNaN(end.getTime()) && end.getTime() < Date.now();
};

const getRolesFromToken = () => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token) return [];

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));

    return payload.roles || [];
  } catch (e) {
    return [];
  }
};

function Dashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastStockUpdateDate, setLastStockUpdateDate] = useState({});

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [open, setOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState(null);
  const [newUser, setNewUser] = useState(EMPTY_USER);
  const [originalUser, setOriginalUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [emailDialog, setEmailDialog] = useState({ open: false, user: null, password: "", sending: false });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const currentUserRoles = getRolesFromToken();
  const isSuperUser = currentUserRoles.includes("superuser");

  // Admins can only see and manage non-admins (pharmacist, preparator, user)
  // Superusers see everyone, but no one can create or grant 'superuser' role
  const canManageRoles = isSuperUser
    ? ["admin", "pharmacist", "preparator"]
    : ["pharmacist", "preparator"];

  const fetchLastStockUpdateDate = async (userId) => {
    try {
      const response = await axios.get(`/latest_stock_update/${userId}`, { headers: authHeaders() });

      return response.data;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get("get_users", { headers: authHeaders() });
        const fetchedUsers = [...response.data].reverse();
        setUsers(fetchedUsers);
        // Show the table straight away, stock dates stream in behind their own spinners
        setLoading(false);

        const stockUpdateDates = await Promise.all(
          fetchedUsers.map(async (user) => ({
            userId: user._id,
            lastStockUpdateDate: await fetchLastStockUpdateDate(user._id),
          }))
        );

        const userToLastStockUpdateDateMap = {};
        stockUpdateDates.forEach(({ userId, lastStockUpdateDate }) => {
          userToLastStockUpdateDateMap[userId] = lastStockUpdateDate || "-";
        });
        setLastStockUpdateDate(userToLastStockUpdateDateMap);
      } catch (error) {
        notify(errorMessage(error, "Unable to load users."), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Keep the current page valid whenever the result set shrinks
  useEffect(() => {
    setPage(0);
  }, [searchQuery, statusFilter]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return users.filter((user) => {
      const matchesQuery =
        !query ||
        [user.username, user.email, user.phone_number, user.taxRegistrationNumber, user.userERP]
          .some((field) => field && String(field).toLowerCase().includes(query));

      const matchesStatus = statusFilter === "all" || statusFilter === getSubscriptionState(user);

      return matchesQuery && matchesStatus;
    });
  }, [users, searchQuery, statusFilter]);

  const stats = useMemo(() => ({
    total: users.length,
    paid: users.filter((user) => getSubscriptionState(user) === "paid").length,
    trial: users.filter((user) => getSubscriptionState(user) === "trial").length,
    inactive: users.filter((user) => getSubscriptionState(user) === "not_subscribed").length,
  }), [users]);

  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const upsertUser = (userId, changes) =>
    setUsers((prevUsers) => prevUsers.map((user) => (user._id === userId ? { ...user, ...changes } : user)));

  const handleOpenMenu = (event, user) => {
    setAnchorEl(event.currentTarget);
    setMenuUser(user);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuUser(null);
  };

  const handleOpenAddDialog = () => {
    setNewUser(EMPTY_USER);
    setOriginalUser(null);
    setConfirmPassword("");
    setChangePassword(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setDialogMode("add");
    setOpen(true);
  };

  const handleOpenUpdateDialog = (user) => {
    if (!user) return;

    setOriginalUser(user);
    // Normalise nullable fields so every input stays controlled
    setNewUser({
      ...user,
      password: "",
      phone_number: user.phone_number || "",
      taxRegistrationNumber: user.taxRegistrationNumber || "",
      userERP: user.userERP || "",
      maxProfiles: user.maxProfiles || "",
      country: user.country || "TN",
      trial_days: user.trial_days == null ? 30 : user.trial_days,
      afficher_traitement_limit: user.afficher_traitement_limit == null ? 50 : user.afficher_traitement_limit,
    });
    setConfirmPassword("");
    setChangePassword(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setDialogMode("update");
    setOpen(true);
    handleCloseMenu();
  };

  // Trial limits only apply until the user moves onto a paid subscription
  const canEditTrialFields =
    dialogMode === "add" || (originalUser && originalUser.subscription_mode !== "paid");

  const isPasswordRequired = dialogMode === "add" || changePassword;
  const passwordsMismatch = isPasswordRequired && newUser.password !== confirmPassword;
  const canSubmit =
    Boolean(newUser.username && newUser.email) &&
    !passwordsMismatch &&
    (!isPasswordRequired || Boolean(newUser.password && confirmPassword));

  const handleAddUser = async () => {
    setSaving(true);
    try {
      const response = await axios.post(
        "auth/signup",
        { ...newUser, email: newUser.email.toLowerCase(), confirmPassword },
        { headers: authHeaders() }
      );

      setUsers((prevUsers) => [{ ...response.data, roles: newUser.roles }, ...prevUsers]);
      setLastStockUpdateDate((prevState) => ({ ...prevState, [response.data._id]: "-" }));
      setNewUser(EMPTY_USER);
      setOpen(false);
      notify(`User ${response.data.username} created.`);
    } catch (error) {
      notify(errorMessage(error, "Failed to create the user."), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    setSaving(true);
    try {
      const updatePromises = [];

      if (changePassword && newUser.password) {
        updatePromises.push(
          axios.put(
            `/admin/users/${newUser._id}/password`,
            { newPassword: newUser.password, confirmNewPassword: confirmPassword },
            { headers: authHeaders() }
          )
        );
      }

      // Treat null / undefined and "" as the same value so untouched fields are not resent
      const hasChanged = (next, previous) => (next == null ? "" : next) !== (previous == null ? "" : previous);

      const credentialUpdates = {};
      if (hasChanged(newUser.username, originalUser.username)) credentialUpdates.username = newUser.username;
      if (hasChanged(newUser.email, originalUser.email)) credentialUpdates.email = newUser.email;
      if (hasChanged(newUser.phone_number, originalUser.phone_number)) credentialUpdates.phone_number = newUser.phone_number;

      const rolesChanged = (next = [], previous = []) =>
        next.length !== previous.length || next.some((role) => !previous.includes(role));
      if (rolesChanged(newUser.roles, originalUser.roles)) credentialUpdates.roles = newUser.roles;

      if (hasChanged(newUser.taxRegistrationNumber, originalUser.taxRegistrationNumber)) {
        credentialUpdates.taxRegistrationNumber = newUser.taxRegistrationNumber;
      }

      if (hasChanged(newUser.userERP, originalUser.userERP)) credentialUpdates.userERP = newUser.userERP;

      // An empty value cannot unset maxProfiles, so only send a usable number
      if (newUser.maxProfiles !== "" && hasChanged(newUser.maxProfiles, originalUser.maxProfiles)) {
        credentialUpdates.maxProfiles = Number(newUser.maxProfiles);
      }

      if (canEditTrialFields) {
        if (hasChanged(newUser.trial_days, originalUser.trial_days)) {
          credentialUpdates.trial_days = Number(newUser.trial_days);
        }

        if (hasChanged(newUser.afficher_traitement_limit, originalUser.afficher_traitement_limit)) {
          credentialUpdates.afficher_traitement_limit = Number(newUser.afficher_traitement_limit);
        }
      }

      if ((newUser.country || "TN") !== (originalUser.country || "TN")) {
        credentialUpdates.country = newUser.country || "TN";
      }

      if (Object.keys(credentialUpdates).length > 0) {
        updatePromises.push(
          axios.put(`/admin/users/${newUser._id}`, credentialUpdates, { headers: authHeaders() })
        );
      }

      if (updatePromises.length === 0) {
        setOpen(false);
        setOriginalUser(null);
        notify("No changes to save.", "info");

        return;
      }

      const responses = await Promise.all(updatePromises);

      // Updates such as a new trial length move the subscription dates, so take
      // them back from the responses rather than keeping the stale local values.
      let updatedSubscription = {};
      responses.forEach((response) => {
        if (response.data && response.data.user) {
          updatedSubscription = response.data.user;
        }
      });

      const { password, ...userWithoutPassword } = newUser;
      upsertUser(newUser._id, { ...userWithoutPassword, ...updatedSubscription });
      setOpen(false);
      setOriginalUser(null);
      notify("User updated.");
    } catch (error) {
      notify(errorMessage(error, "Failed to update the user."), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleActivation = async (user, isActive) => {
    handleCloseMenu();
    if (!user) return;

    try {
      const response = await axios.put(
        "users/activation",
        { userId: user._id, isActive },
        { headers: authHeaders() }
      );

      // The server is authoritative on the new subscription window, so refresh the
      // row from its response instead of guessing the new dates locally.
      const updatedSubscription = (response.data && response.data.user) || { is_subscribed: isActive };
      upsertUser(user._id, updatedSubscription);
      notify(`Subscription ${updatedSubscription.is_subscribed ? "activated" : "deactivated"} for ${user.username}.`);
    } catch (error) {
      notify(errorMessage(error, `Failed to ${isActive ? "activate" : "deactivate"} the subscription.`), "error");
    }
  };

  const handleDermoActivation = async (user, isActive) => {
    handleCloseMenu();
    if (!user) return;

    try {
      await axios.put("activate_dermo", { userId: user._id }, { headers: authHeaders() });
      upsertUser(user._id, { is_dermo_subscribed: isActive });
      notify(`Dermo ${isActive ? "activated" : "deactivated"} for ${user.username}.`);
    } catch (error) {
      notify(errorMessage(error, "Failed to update the dermo subscription."), "error");
    }
  };

  const handleDeleteClick = (user) => {
    if (user) setUserToDelete(user);
    handleCloseMenu();
  };

  const handleDeleteConfirm = async () => {
    const user = userToDelete;
    setUserToDelete(null);
    try {
      await axios.post("delete_user", { userId: user._id }, { headers: authHeaders() });
      setUsers((prevUsers) => prevUsers.filter((item) => item._id !== user._id));
      notify(`User ${user.username} deleted.`);
    } catch (error) {
      notify(errorMessage(error, "Failed to delete the user."), "error");
    }
  };

  const handleOpenEmailDialog = (user) => {
    if (user) setEmailDialog({ open: true, user, password: "", sending: false });
    handleCloseMenu();
  };

  const handleSendWelcomeEmail = async () => {
    const { user, password } = emailDialog;
    setEmailDialog((prev) => ({ ...prev, sending: true }));
    try {
      await axios.post(
        'auth/welcome-email',
        { email: user.email, password, username: user.username },
        { headers: authHeaders() }
      );
      setEmailDialog({ open: false, user: null, password: "", sending: false });
      notify(`Welcome email sent to ${user.email}.`);
    } catch (error) {
      setEmailDialog((prev) => ({ ...prev, sending: false }));
      notify(errorMessage(error, "Failed to send the welcome email."), "error");
    }
  };

  const renderSubscriptionCell = (user) => {
    const state = getSubscriptionState(user);

    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {state === "trial" && <Chip size="small" color="warning" label="Trial" />}
        {state === "paid" && <Chip size="small" color="success" label="Paid" />}
        {state === "not_subscribed" && <Chip size="small" variant="outlined" label="Not subscribed" />}
        {user.is_dermo_subscribed && (
          <Chip size="small" variant="outlined" color="secondary" icon={<SpaIcon />} label="Dermo" />
        )}
      </Stack>
    );
  };

  const renderSubscriptionEnd = (user) => {
    if (!user.subscription_end_date) {
      return user.is_subscribed ? "Ongoing" : "-";
    }

    return formatDate(user.subscription_end_date);
  };

  const renderStockUpdateCell = (userId) => {
    const value = lastStockUpdateDate[userId];
    if (value === undefined) {
      return <CircularProgress size={16} thickness={4} />;
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }

    const today = new Date();
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    return (
      <Typography variant="body2" sx={{ color: isToday ? "inherit" : "#FA520D" }}>
        {date.toLocaleString("en-GB")}
      </Typography>
    );
  };

  const statCards = [
    { label: "Total users", value: stats.total, color: theme.palette.text.primary },
    { label: "Paid", value: stats.paid, color: theme.palette.success.main },
    { label: "On trial", value: stats.trial, color: theme.palette.warning.main },
    { label: "Not subscribed", value: stats.inactive, color: theme.palette.text.secondary },
  ];

  return (
    <Box padding={2}>
      <Typography variant={isMobile ? "h4" : "h3"} gutterBottom>
        Dashboard
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }} useFlexGap>
        {statCards.map((card) => (
          <Paper
            key={card.label}
            variant="outlined"
            sx={{ px: 3, py: 1.5, minWidth: 140, borderRadius: 2, flexGrow: { xs: 1, sm: 0 } }}
          >
            <Typography variant="caption" color="text.secondary" textTransform="uppercase">
              {card.label}
            </Typography>
            <Typography variant="h5" sx={{ color: card.color, fontWeight: 600 }}>
              {loading ? "-" : card.value}
            </Typography>
          </Paper>
        ))}
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          placeholder="Search by name, email, phone, ERP…"
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="disabled" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Subscription</InputLabel>
          <Select
            value={statusFilter}
            label="Subscription"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {SUBSCRIPTION_FILTERS.map((filter) => (
              <MenuItem key={filter.value} value={filter.value}>
                {filter.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          onClick={handleOpenAddDialog}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Add User
        </Button>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 640 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ '& th': { backgroundColor: theme.palette.grey[100], fontWeight: 600 } }}>
                <TableCell>User</TableCell>
                <TableCell>Phone number</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                <TableCell>Latest Stock Update</TableCell>
                <TableCell>Terms</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.map((user) => (
                <TableRow key={user._id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title={user.is_subscribed ? "Account accessible" : "Account blocked"}>
                        {user.is_subscribed
                          ? <LockOpenIcon fontSize="small" color="success" />
                          : <LockIcon fontSize="small" color="error" />}
                      </Tooltip>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {user.username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {user.email}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>{user.phone_number || "-"}</TableCell>
                  <TableCell>{user.country || "TN"}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(user.roles || []).map((role) => (
                        <Chip key={role} size="small" variant="outlined" label={role} />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>{renderSubscriptionCell(user)}</TableCell>
                  <TableCell>{formatDate(user.subscription_start_date)}</TableCell>
                  <TableCell
                    sx={{ color: isSubscriptionLapsed(user) ? "#FA520D" : "inherit" }}
                    title={isSubscriptionLapsed(user) ? "Subscription window has ended" : undefined}
                  >
                    {renderSubscriptionEnd(user)}
                  </TableCell>
                  <TableCell>{renderStockUpdateCell(user._id)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant={user.hasAgreedToTerms ? "filled" : "outlined"}
                      color={user.hasAgreedToTerms ? "success" : "error"}
                      label={user.hasAgreedToTerms ? "Yes" : "No"}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={(e) => handleOpenMenu(e, user)} aria-label="user actions">
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {users.length === 0 ? "No users yet." : "No users match the current filters."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </TableContainer>
      )}

      {/* Row actions */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={() => handleOpenUpdateDialog(menuUser)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Update</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleActivation(menuUser, !(menuUser && menuUser.is_subscribed))}>
          <ListItemIcon>
            {menuUser && menuUser.is_subscribed
              ? <ToggleOffIcon fontSize="small" />
              : <ToggleOnIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {menuUser && menuUser.is_subscribed ? "Deactivate Subscription" : "Activate Subscription"}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDermoActivation(menuUser, !(menuUser && menuUser.is_dermo_subscribed))}>
          <ListItemIcon><SpaIcon fontSize="small" /></ListItemIcon>
          <ListItemText>
            {menuUser && menuUser.is_dermo_subscribed ? "Deactivate Dermo" : "Activate Dermo"}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleOpenEmailDialog(menuUser)}>
          <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Send Welcome Email</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDeleteClick(menuUser)} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* User Dialog Form */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{dialogMode === "add" ? "Add New User" : "Update User"}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="overline" color="text.secondary">
            Account
          </Typography>
          <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
            <TextField
              fullWidth
              required
              label="Username"
              value={newUser.username}
              onChange={(e) => setNewUser((prev) => ({ ...prev, username: e.target.value }))}
            />
            <TextField
              fullWidth
              required
              label="Email"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser((prev) => ({ ...prev, email: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={newUser.phone_number}
              onChange={(e) => setNewUser((prev) => ({ ...prev, phone_number: e.target.value }))}
            />

            {dialogMode === "update" && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={changePassword}
                    onChange={(e) => {
                      setChangePassword(e.target.checked);
                      if (!e.target.checked) {
                        setNewUser((prev) => ({ ...prev, password: "" }));
                        setConfirmPassword("");
                      }
                    }}
                  />
                }
                label="Change password"
              />
            )}

            {isPasswordRequired && (
              <>
                <TextField
                  fullWidth
                  required
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  value={newUser.password}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, password: e.target.value }))}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  required
                  type={showConfirmPassword ? "text" : "password"}
                  label="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={Boolean(confirmPassword) && passwordsMismatch}
                  helperText={Boolean(confirmPassword) && passwordsMismatch ? "Passwords do not match." : " "}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end">
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          </Stack>

          <Typography variant="overline" color="text.secondary">
            Access
          </Typography>
          <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
            <FormGroup row>
              {canManageRoles.map((role) => (
                <FormControlLabel
                  key={role}
                  control={
                    <Checkbox
                      checked={newUser.roles.includes(role)}
                      onChange={(e) =>
                        setNewUser((prev) => ({
                          ...prev,
                          roles: e.target.checked
                            ? [...prev.roles, role]
                            : prev.roles.filter((r) => r !== role),
                        }))
                      }
                    />
                  }
                  label={role.charAt(0).toUpperCase() + role.slice(1)}
                />
              ))}
            </FormGroup>
            <FormControl fullWidth disabled={newUser.roles.includes('superuser')}>
              <InputLabel>Country</InputLabel>
              <Select
                value={newUser.roles.includes('superuser') ? "" : (newUser.country || "TN")}
                label="Country"
                onChange={(e) => setNewUser((prev) => ({ ...prev, country: e.target.value }))}
              >
                <MenuItem value="TN">TN</MenuItem>
                <MenuItem value="DZ">DZ</MenuItem>
                {newUser.roles.includes('superuser') && <MenuItem value="">None (Superuser)</MenuItem>}
              </Select>
            </FormControl>
          </Stack>

          <Typography variant="overline" color="text.secondary">
            Pharmacy
          </Typography>
          <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
            <TextField
              fullWidth
              label={newUser.country === "DZ" ? "CRM ID" : "Matricule fiscale"}
              value={newUser.taxRegistrationNumber}
              onChange={(e) => setNewUser((prev) => ({ ...prev, taxRegistrationNumber: e.target.value }))}
            />
            <TextField
              fullWidth
              label="ERP"
              value={newUser.userERP}
              onChange={(e) => setNewUser((prev) => ({ ...prev, userERP: e.target.value }))}
            />
          </Stack>

          <Typography variant="overline" color="text.secondary">
            Limits
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Maximum Number of Profiles (1 or more)"
              type="number"
              value={newUser.maxProfiles}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow empty string or integers >= 1
                if (value === '' || (parseInt(value) >= 1 && Number.isInteger(Number(value)))) {
                  setNewUser((prev) => ({ ...prev, maxProfiles: value }));
                }
              }}
              inputProps={{ min: 1, step: 1 }}
              helperText={`The user currently has ${((newUser.profiles || []).filter((p) => p.isDeleted === false)).length} active profiles.`}
            />

            {canEditTrialFields && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <TextField
                  label="Trial Period (days)"
                  type="number"
                  value={newUser.trial_days}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, trial_days: e.target.value }))}
                  inputProps={{ min: 0 }}
                />
                <TextField
                  label="Afficher Traitement Limit"
                  type="number"
                  value={newUser.afficher_traitement_limit}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, afficher_traitement_limit: e.target.value }))}
                  inputProps={{ min: 0 }}
                />
                <Button
                  size="small"
                  onClick={() => setNewUser((prev) => ({ ...prev, trial_days: 30, afficher_traitement_limit: 50 }))}
                  sx={{ textTransform: 'none' }}
                >
                  Default
                </Button>
              </Stack>
            )}

            {dialogMode === "update" && (
              <Typography variant="caption" color="text.secondary">
                Current subscription: {formatDate(originalUser && originalUser.subscription_start_date)}
                {" → "}
                {originalUser && !originalUser.subscription_end_date && originalUser.is_subscribed
                  ? "Ongoing"
                  : formatDate(originalUser && originalUser.subscription_end_date)}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={dialogMode === "add" ? handleAddUser : handleUpdateUser}
            disabled={!canSubmit || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {dialogMode === "add" ? "Add" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Welcome email */}
      <Dialog
        open={emailDialog.open}
        onClose={() => setEmailDialog({ open: false, user: null, password: "", sending: false })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Send Welcome Email</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            The credentials below will be emailed to{" "}
            <strong>{emailDialog.user && emailDialog.user.email}</strong>.
          </Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Username"
              value={(emailDialog.user && emailDialog.user.username) || ""}
              InputProps={{ readOnly: true }}
            />
            <TextField
              fullWidth
              autoFocus
              label="Password to send"
              value={emailDialog.password}
              onChange={(e) => setEmailDialog((prev) => ({ ...prev, password: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => setEmailDialog({ open: false, user: null, password: "", sending: false })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendWelcomeEmail}
            disabled={!emailDialog.password || emailDialog.sending}
            startIcon={emailDialog.sending ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(userToDelete)}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete user"
        message={`Are you sure you want to delete ${userToDelete ? userToDelete.username : ""}? This action cannot be undone.`}
        confirmText="Delete"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Dashboard;
